import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { formatDateTimeDMY } from '../utils/dateUtils';
import Layout from '../components/Layout';
import { InlineEdit } from '../components/InlineEdit';
import ApartmentFormBody from '../components/ApartmentFormBody';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  PencilSimple,
  Trash,
  CurrencyEur,
  ArrowLeft,
  CaretDown,
  CaretUp,
  FileText,
  ClockCounterClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import DatePickerDMY from '../components/DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function Apartments() {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const [building, setBuilding] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Apartment dialog
  const [aptDialogOpen, setAptDialogOpen] = useState(false);
  const [editingApt, setEditingApt] = useState(null);
  // Phase 158 — layout aligned with BuildingDetails modal. Fields extended
  // with opening balance (+ date), repairs, phone, email, notes.
  const [aptForm, setAptForm] = useState({
    apartment_number: '', owner_name: '', phone: '', email: '',
    share_percentage: 0, notes: '', monthly_fee: 0,
    opening_balance: 0, opening_balance_date: '', opening_balance_notes: '',
    reserve_fund: 0, repairs_fund: 0,
    // Phase 257 — Tenant + payment responsibility + CC emails.
    tenant_name: '', tenant_phone: '', tenant_email: '',
    payment_responsibility: 'owner',
    extra_emails: [],
    // Phase 654 — Contact person + email recipients (needed by shared ApartmentFormBody).
    contact_name: '', contact_phone: '', contact_email: '',
    email_recipients: { owner: true, contact: false, extras: [] },
  });
  // Active Annual Expense Plan — when present, Monthly Fee is authoritative
  // from the plan and must be rendered read-only (matches BuildingDetails).
  const [plansList, setPlansList] = useState([]);

  // Payment dialog
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payingApt, setPayingApt] = useState(null);
  const [payForm, setPayForm] = useState({ date: new Date().toISOString().split('T')[0], amount: 0, notes: '' });

  // Payment history
  const [historyOpen, setHistoryOpen] = useState(null); // apartment_id or null
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // Reserve Fund History
  const [rfHistoryDialogOpen, setRfHistoryDialogOpen] = useState(false);
  const [rfHistoryApt, setRfHistoryApt] = useState(null);
  const [rfHistory, setRfHistory] = useState([]);

  useEffect(() => { fetchData(); }, [buildingId]);

  const fetchData = async () => {
    try {
      const [buildingRes, aptsRes, summaryRes, plansRes] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}`),
        axios.get(`${API}/buildings/${buildingId}/apartments`),
        axios.get(`${API}/buildings/${buildingId}/apartments/summary`),
        axios.get(`${API}/buildings/${buildingId}/annual-expenses/plans?year=${new Date().getFullYear()}`).catch(() => ({ data: { plans: [] } })),
      ]);
      setBuilding(buildingRes.data);
      setApartments(aptsRes.data);
      setSummary(summaryRes.data);
      setPlansList(plansRes.data?.plans || []);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleInlineAptUpdate = async (aptId, field, value) => {
    try {
      await axios.put(`${API}/apartments/${aptId}`, { [field]: value }, { withCredentials: true });
      toast.success(t('changesSaved'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openReserveFundHistory = async (apt) => {
    setRfHistoryApt(apt);
    try {
      const res = await axios.get(`${API}/apartments/${apt.apartment_id}/reserve-fund-history`);
      setRfHistory(res.data);
      setRfHistoryDialogOpen(true);
    } catch (e) {
      toast.error(t('errorOccurred'));
    }
  };

  // Apartment CRUD
  const handleAptSubmit = async (e) => {
    e.preventDefault();
    const hasActivePlan = plansList.some(p => p.is_active);
    const payload = {
      apartment_number: aptForm.apartment_number,
      owner_name: aptForm.owner_name,
      phone: aptForm.phone || '',
      email: aptForm.email || '',
      notes: aptForm.notes || '',
      share_percentage: parseFloat(aptForm.share_percentage) || 0,
      opening_balance: parseFloat(aptForm.opening_balance) || 0,
      opening_balance_date: aptForm.opening_balance_date || '',
      // Phase 236 — Free-text note describing the opening balance origin.
      opening_balance_notes: aptForm.opening_balance_notes || '',
      reserve_fund: parseFloat(aptForm.reserve_fund) || 0,
      repairs_fund: parseFloat(aptForm.repairs_fund) || 0,
      // Phase 257 — Tenant + payment responsibility + CC emails.
      tenant_name: aptForm.tenant_name || '',
      tenant_phone: aptForm.tenant_phone || '',
      tenant_email: aptForm.tenant_email || '',
      payment_responsibility: aptForm.payment_responsibility || 'owner',
      extra_emails: (aptForm.extra_emails || []).filter(em => (em || '').trim()),
      // Phase 654 — Contact person + email recipients (shared ApartmentFormBody).
      contact_name: aptForm.contact_name || '',
      contact_phone: aptForm.contact_phone || '',
      contact_email: aptForm.contact_email || '',
      email_recipients: aptForm.email_recipients || { owner: true, contact: false, extras: [] },
    };
    // When an Active Annual Expenses plan exists the Monthly Fee is
    // authoritative from the plan (synced server-side). Do NOT overwrite it.
    if (!hasActivePlan) payload.monthly_fee = parseFloat(aptForm.monthly_fee) || 0;

    try {
      if (editingApt) {
        await axios.put(`${API}/apartments/${editingApt.apartment_id}`, payload);
      } else {
        await axios.post(`${API}/buildings/${buildingId}/apartments`, {
          building_id: buildingId,
          ...payload,
          monthly_fee: payload.monthly_fee ?? 0,
        });
      }
      toast.success(t('savedSuccess'));
      setAptDialogOpen(false);
      setEditingApt(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openAptDialog = (apt = null) => {
    setEditingApt(apt);
    setAptForm(apt
      ? {
          apartment_number: apt.apartment_number || '',
          owner_name: apt.owner_name || '',
          phone: apt.phone || '',
          email: apt.email || '',
          share_percentage: apt.share_percentage || 0,
          notes: apt.notes || '',
          monthly_fee: apt.monthly_fee || 0,
          opening_balance: apt.opening_balance || 0,
          opening_balance_date: apt.opening_balance_date || '',
          opening_balance_notes: apt.opening_balance_notes || '',
          reserve_fund: apt.reserve_fund || 0,
          repairs_fund: apt.repairs_fund || 0,
          // Phase 257 — Tenant + payment responsibility + CC emails.
          tenant_name: apt.tenant_name || '',
          tenant_phone: apt.tenant_phone || '',
          tenant_email: apt.tenant_email || '',
          payment_responsibility: apt.payment_responsibility || 'owner',
          extra_emails: apt.extra_emails || [],
          // Phase 654 — Contact person + email recipients
          contact_name: apt.contact_name || '',
          contact_phone: apt.contact_phone || '',
          contact_email: apt.contact_email || '',
          email_recipients: apt.email_recipients || { owner: true, contact: false, extras: [] },
        }
      : {
          apartment_number: '', owner_name: '', phone: '', email: '',
          share_percentage: 0, notes: '', monthly_fee: 0,
          opening_balance: 0, opening_balance_date: '', opening_balance_notes: '',
          reserve_fund: 0, repairs_fund: 0,
          tenant_name: '', tenant_phone: '', tenant_email: '',
          payment_responsibility: 'owner',
          extra_emails: [],
          contact_name: '', contact_phone: '', contact_email: '',
          email_recipients: { owner: true, contact: false, extras: [] },
        }
    );
    setAptDialogOpen(true);
  };

  // Payment
  const handlePaySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/apartments/${payingApt.apartment_id}/payments`, {
        apartment_id: payingApt.apartment_id,
        building_id: buildingId,
        date: payForm.date,
        amount: parseFloat(payForm.amount) || 0,
        notes: payForm.notes,
      });
      toast.success(t('savedSuccess'));
      setPayDialogOpen(false);
      setPayingApt(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openPayDialog = (apt) => {
    setPayingApt(apt);
    setPayForm({ date: new Date().toISOString().split('T')[0], amount: apt.monthly_fee, notes: '' });
    setPayDialogOpen(true);
  };

  // Payment History
  const toggleHistory = async (aptId) => {
    if (historyOpen === aptId) {
      setHistoryOpen(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/apartments/${aptId}/payments`);
      setPaymentHistory(res.data);
      setHistoryOpen(aptId);
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      if (deleteType === 'apartment') {
        await axios.delete(`${API}/apartments/${deletingItem.apartment_id}`);
      } else {
        await axios.delete(`${API}/apartment-payments/${deletingItem.payment_id}`);
      }
      toast.success(t('deletedSuccess'));
      setDeleteDialogOpen(false);
      setDeletingItem(null);
      fetchData();
      if (deleteType === 'payment' && historyOpen) toggleHistory(historyOpen);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-50 text-emerald-700 border-emerald-300';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-300';
      case 'overdue': return 'bg-red-50 text-red-700 border-red-300';
      default: return 'bg-zinc-50 text-zinc-700 border-zinc-300';
    }
  };

  const getRowBg = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-50/40';
      case 'overdue': return 'bg-red-50/40';
      default: return '';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="apartments-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/buildings')} className="rounded-none" data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              {building?.name} — {t('apartments')}
            </h1>
            <p className="text-sm text-zinc-500">{building?.location}</p>
          </div>
        </div>

        {/* Summary — Financial Overview (Phase 443) */}
        {summary && (
          <section className="mb-6" data-testid="apartments-summary">
            {/* Primary stats row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <div className="bg-white border border-zinc-200 p-4" data-testid="apartment-count-card">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t('apartments')}</p>
                <p className="text-2xl font-bold font-mono mt-1">{summary.apartment_count}</p>
              </div>
              <div className="bg-white border border-blue-200 p-4" data-testid="expected-income-card">
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">{t('totalExpectedIncome')}</p>
                <p className="text-2xl font-bold font-mono text-blue-700 mt-1">&euro;{summary.total_expected.toFixed(2)}</p>
              </div>
              <div className="bg-white border border-emerald-200 p-4" data-testid="collected-card">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">{t('totalCollected')}</p>
                <p className="text-2xl font-bold font-mono text-emerald-700 mt-1">&euro;{summary.total_collected.toFixed(2)}</p>
              </div>
              <div className="bg-white border border-red-200 p-4" data-testid="outstanding-card">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-600">{t('totalOutstanding')}</p>
                <p className="text-2xl font-bold font-mono text-red-700 mt-1">&euro;{summary.total_outstanding.toFixed(2)}</p>
                <p className="text-[11px] text-zinc-500 mt-1" data-testid="apartments-owing-meta">
                  {t('apartmentsOwing') || 'Apartments owing'}: <span className="font-mono font-semibold text-red-700">{summary.apartments_owing || 0}</span> {t('of') || 'of'} <span className="font-mono">{summary.apartment_count}</span>
                </p>
              </div>
            </div>

            {/* Secondary stats row — Credit balance (Phase 443) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white border border-teal-200 p-4 flex items-center justify-between" data-testid="credit-balance-card">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-teal-600">{t('totalCreditBalance') || 'Total Credit Balance'}</p>
                  <p className="text-2xl font-bold font-mono text-teal-700 mt-1">+&euro;{(summary.total_credit || 0).toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-zinc-500">{t('apartmentsWithCredit') || 'Apartments with credit'}</p>
                  <p className="font-mono font-semibold text-teal-700">{summary.apartments_with_credit || 0} {t('of') || 'of'} {summary.apartment_count}</p>
                </div>
              </div>
              {/* Net position card */}
              <div className="bg-white border border-zinc-300 p-4 flex items-center justify-between" data-testid="net-position-card">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">{t('netPosition') || 'Net Position'}</p>
                  <p className={`text-2xl font-bold font-mono mt-1 ${(summary.total_outstanding - (summary.total_credit || 0)) > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                    {(summary.total_outstanding - (summary.total_credit || 0)) >= 0 ? '' : '+'}&euro;{(summary.total_outstanding - (summary.total_credit || 0)).toFixed(2)}
                  </p>
                </div>
                <p className="text-[11px] text-zinc-500 max-w-[160px] text-right">{t('netPositionDesc') || 'Outstanding minus credits — what you actually need to collect.'}</p>
              </div>
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="flex justify-end mb-4">
          <Button onClick={() => openAptDialog()} className="bg-zinc-950 rounded-none" data-testid="add-apartment-btn">
            <Plus size={18} className="mr-2" />{t('addApartment')}
          </Button>
        </div>

        {/* Share Warning */}
        {apartments.length > 0 && (() => {
          const totalShare = apartments.reduce((s, a) => s + (a.share_percentage || 0), 0);
          const isValid = Math.abs(totalShare - 100) < 0.01;
          if (isValid) return null;
          return (
            <div className={`border px-4 py-3 text-sm flex items-center gap-2 mb-4 ${totalShare > 100 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`} data-testid="share-warning">
              <span>
                {t('sharePercentage')} {t('total')}: <strong className="font-mono">{totalShare.toFixed(2)}%</strong> — {totalShare < 100 ? t('shareUnder100') : t('shareOver100')}
              </span>
            </div>
          );
        })()}

        {/* Desktop Table */}
        <div className="hidden md:block bg-white border border-zinc-200 overflow-x-auto">
          {apartments.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">{t('noApartments')}</div>
          ) : (
            <table className="excel-table" data-testid="apartments-table">
              <thead>
                <tr>
                  <th>{t('apartmentNumber')}</th>
                  <th>{t('ownerName')}</th>
                  <th>{t('monthlyFee')}</th>
                  <th>{t('commonExpenses')}</th>
                  <th>{t('reserveFund')}</th>
                  <th>{t('sharePercentage')}</th>
                  <th>{t('totalPaid')}</th>
                  <th>{t('remainingBalance')}</th>
                  <th>{t('status')}</th>
                  <th className="w-40">{t('edit')}</th>
                </tr>
              </thead>
              <tbody>
                {apartments.map((apt) => (
                  <React.Fragment key={apt.apartment_id}>
                    <tr className={getRowBg(apt.status)} data-testid={`apartment-row-${apt.apartment_id}`}>
                      <td className="font-semibold">{apt.apartment_number}</td>
                      <td className="text-sm">
                        <InlineEdit
                          value={apt.owner_name}
                          onSave={(val) => handleInlineAptUpdate(apt.apartment_id, 'owner_name', val)}
                          placeholder="-"
                        />
                      </td>
                      <td>
                        <InlineEdit
                          value={apt.monthly_fee}
                          onSave={(val) => handleInlineAptUpdate(apt.apartment_id, 'monthly_fee', val)}
                          type="number"
                          prefix="€"
                          mono
                        />
                      </td>
                      <td>
                        <InlineEdit
                          value={apt.common_expenses || 0}
                          onSave={(val) => handleInlineAptUpdate(apt.apartment_id, 'common_expenses', val)}
                          type="number"
                          prefix="€"
                          mono
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <InlineEdit
                            value={apt.reserve_fund || 0}
                            onSave={(val) => handleInlineAptUpdate(apt.apartment_id, 'reserve_fund', val)}
                            type="number"
                            prefix="€"
                            mono
                          />
                          <button
                            onClick={() => openReserveFundHistory(apt)}
                            className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors flex-shrink-0"
                            title={t('reserveFundHistory')}
                            data-testid={`rf-history-${apt.apartment_id}`}
                          >
                            <ClockCounterClockwise size={14} />
                          </button>
                        </div>
                      </td>
                      <td>
                        <InlineEdit
                          value={apt.share_percentage || 0}
                          onSave={(val) => handleInlineAptUpdate(apt.apartment_id, 'share_percentage', val)}
                          type="number"
                          suffix="%"
                          mono
                        />
                      </td>
                      <td className="font-mono text-emerald-700">&euro;{apt.total_paid.toFixed(2)}</td>
                      <td className={`font-mono font-semibold ${apt.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        &euro;{apt.balance.toFixed(2)}
                      </td>
                      <td>
                        <span className={`px-2 py-0.5 text-xs font-mono uppercase border ${getStatusBadge(apt.status)}`}>
                          {t(apt.status)}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openPayDialog(apt)}
                            className="p-2 hover:bg-emerald-50 text-emerald-600 transition-colors"
                            title={t('addPayment')}
                            data-testid={`add-payment-${apt.apartment_id}`}
                          >
                            <CurrencyEur size={18} />
                          </button>
                          <button
                            onClick={() => toggleHistory(apt.apartment_id)}
                            className={`p-2 hover:bg-zinc-100 transition-colors ${historyOpen === apt.apartment_id ? 'bg-zinc-100' : ''}`}
                            title={t('paymentHistory')}
                            data-testid={`toggle-history-${apt.apartment_id}`}
                          >
                            {historyOpen === apt.apartment_id ? <CaretUp size={18} /> : <CaretDown size={18} />}
                          </button>
                          <button
                            onClick={() => navigate(`/apartment-statement?building=${buildingId}&apartment=${apt.apartment_id}`)}
                            className="p-2 hover:bg-blue-50 text-blue-600 transition-colors"
                            title={t('apartmentStatement')}
                            data-testid={`stmt-apartment-${apt.apartment_id}`}
                          >
                            <FileText size={18} />
                          </button>
                          <button
                            onClick={() => openAptDialog(apt)}
                            className="p-2 hover:bg-zinc-100 transition-colors"
                            title={t('edit')}
                            data-testid={`edit-apartment-${apt.apartment_id}`}
                          >
                            <PencilSimple size={18} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => { setDeletingItem(apt); setDeleteType('apartment'); setDeleteDialogOpen(true); }}
                              className="p-2 hover:bg-red-50 text-red-600 transition-colors"
                              title={t('delete')}
                              data-testid={`delete-apartment-${apt.apartment_id}`}
                            >
                              <Trash size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expandable payment history */}
                    {historyOpen === apt.apartment_id && (
                      <tr>
                        <td colSpan="10" className="p-0">
                          <div className="bg-zinc-50 border-t border-zinc-200 p-4">
                            <h4 className="text-sm font-semibold mb-3">{t('paymentHistory')} — {apt.apartment_number}</h4>
                            {paymentHistory.length === 0 ? (
                              <p className="text-sm text-zinc-400">{t('noPayments')}</p>
                            ) : (
                              <div className="space-y-2">
                                {paymentHistory.map((pay) => (
                                  <div key={pay.payment_id} className="flex items-center justify-between bg-white border border-zinc-200 px-4 py-2" data-testid={`payment-${pay.payment_id}`}>
                                    <div className="flex gap-6">
                                      <span className="font-mono text-sm">{formatDateDMY(pay.date)}</span>
                                      <span className="font-mono font-semibold text-emerald-700">&euro;{pay.amount.toFixed(2)}</span>
                                      {pay.notes && <span className="text-sm text-zinc-500">{pay.notes}</span>}
                                    </div>
                                    {isAdmin && (
                                      <button
                                        onClick={() => { setDeletingItem(pay); setDeleteType('payment'); setDeleteDialogOpen(true); }}
                                        className="p-1 hover:bg-red-50 text-red-500 transition-colors"
                                        data-testid={`delete-payment-${pay.payment_id}`}
                                      >
                                        <Trash size={14} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {apartments.length === 0 ? (
            <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('noApartments')}</div>
          ) : (
            apartments.map((apt) => (
              <div key={apt.apartment_id} className={`mobile-card ${apt.status === 'overdue' ? 'border-red-300' : apt.status === 'paid' ? 'border-emerald-300' : ''}`} data-testid={`apartment-card-${apt.apartment_id}`}>
                <div className="mobile-card-header">
                  <div>
                    <h3 className="mobile-card-title">{apt.apartment_number}</h3>
                    {apt.owner_name && <p className="text-sm text-zinc-500">{apt.owner_name}</p>}
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-mono uppercase border ${getStatusBadge(apt.status)}`}>
                    {t(apt.status)}
                  </span>
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('monthlyFee')}</span>
                    <span className="font-mono">&euro;{apt.monthly_fee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('totalPaid')}</span>
                    <span className="font-mono text-emerald-700">&euro;{apt.total_paid.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-zinc-100 pt-2">
                    <span>{t('remainingBalance')}</span>
                    <span className={`font-mono ${apt.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      &euro;{apt.balance.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-100">
                  <Button size="sm" onClick={() => openPayDialog(apt)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-none">
                    <CurrencyEur size={16} className="mr-1" />{t('addPayment')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openAptDialog(apt)} className="flex-1 rounded-none">
                    <PencilSimple size={14} className="mr-1" />{t('edit')}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Apartment Dialog — Phase 158: layout identical to BuildingDetails */}
        {/* ===== Add/Edit Apartment Dialog — Phase 654 =====
            Live Buildings → Apartments workflow now uses the SAME shared
            ApartmentFormBody as the other entry points. Wide landscape
            (1400px / 95vw / 90vh) two-column layout. Add = empty values,
            Edit = pre-filled. No old narrow form anywhere. =====
        */}
        <Dialog open={aptDialogOpen} onOpenChange={setAptDialogOpen}>
          <DialogContent className="rounded-none p-0 flex flex-col w-[95vw] max-w-[1400px] max-h-[90vh] sm:!max-w-[1400px]" data-testid="apt-form-dialog">
            <DialogHeader className="px-5 py-3 border-b border-zinc-200 shrink-0 bg-zinc-50/60">
              <div className="flex items-baseline justify-between gap-4">
                <DialogTitle style={{ fontFamily: 'var(--font-heading)' }} className="text-base font-bold tracking-tight">
                  {editingApt ? t('editApartment') : t('addApartment')}
                </DialogTitle>
                {editingApt && (
                  <p className="text-[11px] text-zinc-500 font-mono">
                    {editingApt.apartment_number ? `#${editingApt.apartment_number}` : ''}{building?.name ? ` · ${building.name}` : ''}
                  </p>
                )}
              </div>
            </DialogHeader>
            <form onSubmit={handleAptSubmit} className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto px-5 py-4 bg-zinc-50/30">
                <ApartmentFormBody
                  form={aptForm}
                  setForm={setAptForm}
                  plansList={plansList}
                  t={t}
                  apartmentIdHint={editingApt?.apartment_id}
                />
              </div>
              <div className="border-t border-zinc-200 bg-white px-5 py-2.5 flex items-center justify-end gap-2 shrink-0">
                <Button type="button" variant="outline" onClick={() => setAptDialogOpen(false)} className="rounded-none h-8 text-xs px-3">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none h-8 text-xs px-4" data-testid="apt-form-submit">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>



        {/* Record Payment Dialog */}
        <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('recordPayment')} — {payingApt?.apartment_number}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePaySubmit} className="space-y-4">
              {payingApt && (
                <div className="bg-zinc-50 border border-zinc-200 p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('monthlyFee')}</span>
                    <span className="font-mono">&euro;{payingApt.monthly_fee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('remainingBalance')}</span>
                    <span className={`font-mono font-semibold ${payingApt.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      &euro;{payingApt.balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('expenseDate')}</Label>
                  <DatePickerDMY value={payForm.date} onChange={v => setPayForm({ ...payForm, date: v })} className="mt-1 font-mono" required data-testid="payment-date-input" />
                </div>
                <div>
                  <Label>{t('paymentAmount')} (&euro;)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: parseFloat(e.target.value) || 0 })}
                    className="mt-1 rounded-none font-mono"
                    required
                    data-testid="payment-amount-input"
                  />
                </div>
              </div>
              <div>
                <Label>{t('notes')}</Label>
                <Textarea
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={2}
                  data-testid="payment-notes-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 rounded-none" data-testid="save-payment-btn">
                  <CurrencyEur size={18} className="mr-2" />{t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{deleteType === 'apartment' ? t('deleteApartment') : t('delete')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDelete')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reserve Fund History Dialog */}
        <Dialog open={rfHistoryDialogOpen} onOpenChange={setRfHistoryDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('reserveFundHistory')} — {rfHistoryApt?.apartment_number}
              </DialogTitle>
            </DialogHeader>
            {rfHistory.length === 0 ? (
              <p className="text-center text-zinc-500 py-6">{t('noChanges')}</p>
            ) : (
              <table className="excel-table" data-testid="rf-history-table">
                <thead>
                  <tr>
                    <th className="text-xs">{t('changedAt')}</th>
                    <th className="text-xs text-right">{t('previousAmount')}</th>
                    <th className="text-xs text-right">{t('newAmount')}</th>
                    <th className="text-xs">{t('changedBy')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rfHistory.map((h) => (
                    <tr key={h.history_id} data-testid={`rf-history-row-${h.history_id}`}>
                      <td className="font-mono text-xs">{formatDateTimeDMY(h.changed_at)}</td>
                      <td className="font-mono text-sm text-right">&euro;{(h.previous_amount || 0).toFixed(2)}</td>
                      <td className="font-mono text-sm text-right font-semibold">&euro;{(h.new_amount || 0).toFixed(2)}</td>
                      <td className="text-xs text-zinc-500">{h.changed_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
